import React, {useContext, useEffect, useState} from "react";
import {Box, Typography, makeStyles, Link, List, ListItem, ListItemText, ListItemIcon} from "@material-ui/core";
import PropTypes from "prop-types";
import {useCurrency, useI18N, useLocation} from "../../../common/common";
import {useBreakpoints, useKeycloakFunctions} from "../../../hooks";
import {MARKET_PLACE, MARKET_PLACE_SHARES, BECOME_MEMBER, MEMBERSHIP, DIRECT_SALES_ORDER} from "../../../common/routes";
import CustomButton from "../../widget/button/CustomButton";
import ActionCardsDialog from "../../widget/dialog/ActionCardsDialog";
import ActionCard from "../../widget/card/ActionCard";
import {SHARES_CURRENCY, black_circle} from "../../../common/constant";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import SelectSharesDialog from "./SelectSharesDialog";
import DownloadContractStepperDialog from "../../view/marketplace/DownloadContractStepperDialog";
import { checkDirectSalesDisabled } from "../../../common/configcat/functions";
import BlockBuyingSharesDialog from "../../view/marketplace/BlockBuyingSharesDialog";
import { useBlockBuyingSharesDialogContent } from "../../../hooks/useBlockBuyingSharesDialogContent";
import { LocaleContext } from "../../../store/providers/LocaleProvider";

const STORAGE_KEY_DIRECT_SALES_INITIATED = "isDirectSalesInitiated";

const useStyles = makeStyles(theme => ({
    ml1: {
        marginLeft: theme.spacing(1)
    },
    textMedium: {
        color: theme.palette.text.medium,
    },
    list: {
        "& .MuiListItemIcon-root": {
            color: "unset !important",
            minWidth: theme.spacing(2)
        },
        "& .MuiListItemIcon-alignItemsFlexStart": {
            marginTop: theme.spacing(0.5)
        },
        "& .MuiListItemText-root": {
            marginTop: 0,
            marginBottom: 0
        }
    },
}));

const BuyAShareDialog = ({
    isOpen,
    onClose,
    averagePrice,
    averagePriceForDirect,
    convertedAveragePrice,
    currency,
}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {navigate} = useLocation();
    const {authenticated, userId, country} = useKeycloakFunctions();
    const {formatCurrency} = useCurrency();
    const {sendCustomEvent, eventNames} = useAnalytics();
    const isDirectSalesInitiated = sessionStorage.getItem(STORAGE_KEY_DIRECT_SALES_INITIATED);
    const {isExtraLargeScreen} = useBreakpoints();
    const {
        fetchBlockingBuyingSharesDialogContent,
        blockingBuyingSharesDialogContentDetails,
        targetPages
    } = useBlockBuyingSharesDialogContent();
    const {localeUppercase} = useContext(LocaleContext)[0];

    const [isOpenSelectSharesDialog, setOpenSelectSharesDialog] = useState(false);
    const [isOpenDownloadContractStepperDialog, setOpenDownloadContractStepperDialog] = useState(false);
    const [isDirectSalesDisabled, setDirectSalesDisabled] = useState(true);
    const [isBlockBuyingSharesDialogOpen, setBlockBuyingSharesDialogOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            sendCustomEvent({
                event_name: eventNames.BUYSHARE_POPUP_IMPRESSION
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (!authenticated) {
            return;
        }

        checkDirectSalesDisabled(userId)
            .then(async (res) => {
                const directSalesDisabled = res;
                let blockingBuyingSharesDialogContent = {};
                setDirectSalesDisabled(res);
                if (!directSalesDisabled) {
                    blockingBuyingSharesDialogContent = await fetchBlockingBuyingSharesDialogContent(country, targetPages.direct_sale, localeUppercase);
                }
                if (isDirectSalesInitiated) {
                    if (!directSalesDisabled) {
                        if (blockingBuyingSharesDialogContent) {
                            handleOpenBlockBuyingSharesDialog();
                        } else {
                            handleOpenSelectSharesDialog();
                        }
                    } else {
                        handleOpenDownloadContractStepperDialog();
                    }
                }
                sessionStorage.removeItem(STORAGE_KEY_DIRECT_SALES_INITIATED);
            });
    }, [authenticated, isDirectSalesInitiated, localeUppercase, country]);

    const handleGoToMarketPlace = () => {
        sendCustomEvent({
            event_name: eventNames.BUYSHARE_POPUP_TOMARKETPLACE
        });
        if (authenticated) {
            navigate(MARKET_PLACE + MARKET_PLACE_SHARES);
        } else {
            navigate(BECOME_MEMBER);
        }
    };

    const handleGoToBuySharesFromMembers = () => {
        sendCustomEvent({
            event_name: eventNames.BUYSHARE_POPUP_MOREABOUT_MRKT
        });
        window.open("https://help.hapimag.com/s/topic/0TO09000000CjKFGA0/buy-shares-from-members", "_blank");
    };

    const handleGoToBuySharesFromHapimag = () => {
        sendCustomEvent({
            event_name: eventNames.BUYSHARE_POPUP_MOREABOUT_HPMG
        });
        window.open("https://help.hapimag.com/s/topic/0TO09000000CjKPGA0/buy-shares-from-hapimag", "_blank");
    };


    const handleOpenSelectSharesDialog = () => {
        setOpenSelectSharesDialog(true);
    };

    const handleCloseSelectSharesDialog = () => {
        setOpenSelectSharesDialog(false);
    };

    const handleClose = () => {
        sendCustomEvent({
            event_name: eventNames.BUYSHARE_POPUP_CLOSE
        });
        onClose();
    };

    const handlePurchaseDirectly = () => {
        if (authenticated && blockingBuyingSharesDialogContentDetails) {
            onClose();
            handleOpenBlockBuyingSharesDialog();
            return;
        }

        if(!isDirectSalesDisabled) {
            if (authenticated) {
                onClose();
                handleOpenSelectSharesDialog();
            } else {
                // Set a session storage variable so after login/register we can use it to open the dialog
                if(!isDirectSalesInitiated) {
                    sessionStorage.setItem(STORAGE_KEY_DIRECT_SALES_INITIATED, true);
                }
                navigate(BECOME_MEMBER + `?returnUrl=${encodeURIComponent(window.location.origin + MEMBERSHIP)}` + "&directSales=true");
            }
        } else {
            if (authenticated) {
                onClose();
                handleOpenDownloadContractStepperDialog();
            } else {
                // Set a session storage variable so after login/register we can use it to open the dialog
                if(!isDirectSalesInitiated) {
                    sessionStorage.setItem(STORAGE_KEY_DIRECT_SALES_INITIATED, true);
                }
                navigate(BECOME_MEMBER + `?returnUrl=${encodeURIComponent(window.location.origin + MEMBERSHIP)}` + "&directSales=true");
            }
        }
    };

    const handleOpenDownloadContractStepperDialog = () => {
        setOpenDownloadContractStepperDialog(true);
    };

    const handleCloseDownloadContractStepperDialog = () => {
        setOpenDownloadContractStepperDialog(false);
    };

    const handleOpenBlockBuyingSharesDialog = () => {
        setBlockBuyingSharesDialogOpen(true);
    };

    const handleCloseBlockBuyingSharesDialog = () => {
        setBlockBuyingSharesDialogOpen(false);
    };

    return (
        <>
            <ActionCardsDialog
                reduceMargins={!isExtraLargeScreen()}
                open={isOpen}
                handleClose={handleClose}
                disableBackdropClick={false}
                hasCloseButton
                maxWidth="md"
                title={i18n("view.buy_a_share.title")}
                description={
                    <Box
                        mb={5}
                    >
                        <Typography
                            variant={"body1"}
                            align={"center"}
                        >
                            {i18n("view.buy_a_share.description")}
                        </Typography>
                    </Box>
                }
                cards={[
                    <ActionCard
                        key={1}
                        image="/images/onboarding-buy-from-hapimag.png"
                        title={i18n("view.buy_a_share.directly_from_hapimag")}
                        description={
                            <Box px={2}>
                                {averagePriceForDirect &&
                                    <>
                                        <Typography
                                            component="div"
                                            variant="h5"
                                            align="center"
                                        >
                                            {
                                                `${currency} ${currency !== "CHF"
                                                    ? averagePriceForDirect?.converted_price
                                                    : averagePriceForDirect?.original_price
                                                }`}
                                        </Typography>
                                        <Typography
                                            component="div"
                                            variant="caption"
                                            align="center"
                                            className={classes.textMedium}
                                        >
                                            {currency === "CHF"
                                                ? i18n("view.buy_a_share.share_price")
                                                : i18n("view.buy_a_share.share_price_with_currency", {
                                                    price: formatCurrency("CHF", averagePriceForDirect?.original_price)
                                                })
                                            }
                                        </Typography>
                                    </>
                                }
                                <List
                                    dense
                                    className={classes.list}
                                >
                                    <ListItem
                                        alignItems="flex-start"
                                        disableGutters
                                    >
                                        <ListItemIcon>
                                            {black_circle}
                                        </ListItemIcon>
                                        <ListItemText disableTypography>
                                            <Typography variant="body1">
                                                {i18n("view.buy_a_share.detail_1")}
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                    <ListItem
                                        alignItems="flex-start"
                                        disableGutters
                                    >
                                        <ListItemIcon>
                                            {black_circle}
                                        </ListItemIcon>
                                        <ListItemText disableTypography>
                                            <Typography variant="body1">
                                                {i18n("view.buy_a_share.detail_2")}
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                    <ListItem
                                        alignItems="flex-start"
                                        disableGutters
                                    >
                                        <ListItemIcon>
                                            {black_circle}
                                        </ListItemIcon>
                                        <ListItemText disableTypography>
                                            <Typography variant="body1">
                                                {i18n("view.buy_a_share.detail_3")}
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                </List>
                                <Box
                                    mt={3}
                                    mb={0.5}
                                >
                                    <Typography
                                        variant="button"
                                    >
                                        <Link
                                            onClick={handleGoToBuySharesFromHapimag}
                                        >
                                            <Typography
                                                variant="body2"
                                            >
                                                {i18n("common.label.learn_more")}
                                            </Typography>
                                        </Link>
                                    </Typography>
                                </Box>
                            </Box>
                        }
                        cta={
                            <CustomButton
                                variant={"contained"}
                                fullWidth
                                disableElevation
                                onClick={handlePurchaseDirectly}
                            >
                                {i18n("view.buy_a_share.title")}
                            </CustomButton>
                        }
                    />,
                    <ActionCard
                        key={2}
                        image="/images/onboarding-to-marketplace-who-sells-shares-illustration.png"
                        title={i18n("view.buy_a_share.card_buy_on_your_own.title")}
                        description={
                            <Box px={2}>
                                {averagePriceForDirect &&
                                    <>
                                        <Typography
                                            component="div"
                                            variant="h5"
                                            align="center"
                                        >
                                            {`${currency} ${convertedAveragePrice ?? averagePrice}`}
                                        </Typography>
                                        <Typography
                                            component="div"
                                            variant="caption"
                                            align="center"
                                            className={classes.textMedium}
                                        >
                                            {!convertedAveragePrice
                                                ? i18n("view.buy_a_share.card_buy_on_your_own.avg_price_per_share")
                                                : i18n("view.investment.average_price_label", {
                                                    price: formatCurrency(SHARES_CURRENCY, averagePrice)
                                                })
                                            }
                                        </Typography>
                                    </>
                                }
                                <List
                                    dense
                                    className={classes.list}
                                >
                                    <ListItem
                                        alignItems="flex-start"
                                        disableGutters
                                    >
                                        <ListItemIcon>
                                            {black_circle}
                                        </ListItemIcon>
                                        <ListItemText disableTypography>
                                            <Typography variant="body1">
                                                {i18n("view.buy_a_share.card_buy_on_your_own.detail_1")}
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                    <ListItem
                                        alignItems="flex-start"
                                        disableGutters
                                    >
                                        <ListItemIcon>
                                            {black_circle}
                                        </ListItemIcon>
                                        <ListItemText disableTypography>
                                            <Typography variant="body1">
                                                {i18n("view.buy_a_share.card_buy_on_your_own.detail_2")}
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                    <ListItem
                                        alignItems="flex-start"
                                        disableGutters
                                    >
                                        <ListItemIcon>
                                            {black_circle}
                                        </ListItemIcon>
                                        <ListItemText disableTypography>
                                            <Typography variant="body1">
                                                {i18n("view.buy_a_share.card_buy_on_your_own.detail_3")}
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                </List>
                                <Box
                                    mt={3}
                                    mb={0.5}
                                >
                                    <Typography
                                        variant="button"
                                    >
                                        <Link
                                            onClick={handleGoToBuySharesFromMembers}
                                        >
                                            <Typography
                                                variant="body2"
                                            >
                                                {i18n("common.label.learn_more")}
                                            </Typography>
                                        </Link>
                                    </Typography>
                                </Box>
                            </Box>
                        }
                        cta={
                            <CustomButton
                                variant={"contained"}
                                fullWidth
                                disableElevation
                                onClick={handleGoToMarketPlace}
                            >
                                {i18n("view.buy_a_share.card_buy_on_your_own.go_to_the_marketplace")}
                            </CustomButton>
                        }
                    />
                ]}
            />

            {!isDirectSalesDisabled && isOpenSelectSharesDialog && (
                <SelectSharesDialog
                    isOpen={isOpenSelectSharesDialog}
                    onClose={handleCloseSelectSharesDialog}
                    hasCloseButton
                    maxWidth="sm"
                    redirectUrl={DIRECT_SALES_ORDER}
                />
            )}

            {isDirectSalesDisabled && isOpenDownloadContractStepperDialog && (
                <DownloadContractStepperDialog
                    isOpen={isOpenDownloadContractStepperDialog}
                    onClose={handleCloseDownloadContractStepperDialog}
                />
            )}

            {blockingBuyingSharesDialogContentDetails && isBlockBuyingSharesDialogOpen && (
                <BlockBuyingSharesDialog
                    isOpen={isBlockBuyingSharesDialogOpen}
                    onClose={handleCloseBlockBuyingSharesDialog}
                    content={blockingBuyingSharesDialogContentDetails}
                />
            )}
        </>
    );
};

BuyAShareDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    averagePrice: PropTypes.number,
    averagePriceForDirect: PropTypes.object,
    convertedAveragePrice: PropTypes.number,
    currency: PropTypes.string,
};

BuyAShareDialog.defaultProps = {
    isDirectSalesDisabled: true // TODO: when direct sales is rolled out, this should default to false
};

export default BuyAShareDialog;
